import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { getData } from "../../axios/axiosHelper";
import {
  buildAccountApiQuery,
  buildAccountQuery,
  hasActiveAccountFilters,
  normalizeAccountFilters
} from "./query";

const errorMessage = (error) =>
  error?.response?.data?.message || "Unable to load accounts";

const useAccountList = (type) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(
    () => normalizeAccountFilters(searchParams),
    [searchParams]
  );
  const [searchDraft, setSearchDraft] = useState(filters.search || "");
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: filters.page,
    totalPages: 1,
    totalResults: 0
  });
  const [loading, setLoading] = useState(true);
  const endpoint = type === "customers" ? "/user" : "/seller";

  useEffect(() => {
    setSearchDraft(filters.search || "");
  }, [filters.search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const normalized = searchDraft.trim();
      if (normalized === (filters.search || "")) return;
      setSearchParams(
        buildAccountQuery({ ...filters, search: normalized, page: 1 }),
        { replace: true }
      );
    }, 350);
    return () => window.clearTimeout(timer);
  }, [filters, searchDraft, setSearchParams]);

  const load = useCallback(() => {
    setLoading(true);
    setRows([]);
    const query = buildAccountApiQuery(filters).toString();
    getData(`${endpoint}?${query}`, true)
      .then((response) => {
        setRows(response?.data?.data || []);
        setPagination(
          response?.data?.pagination || {
            currentPage: filters.page,
            totalPages: 1,
            totalResults: 0
          }
        );
      })
      .catch((error) => toast.error(errorMessage(error)))
      .finally(() => setLoading(false));
  }, [endpoint, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const updateFilters = (patch) => {
    setSearchParams(
      buildAccountQuery({ ...filters, ...patch, page: patch.page || 1 }),
      { replace: true }
    );
  };

  const clearFilters = () => {
    setSearchDraft("");
    setSearchParams(
      buildAccountQuery({ page: 1, perPage: 10, sort: "-registeredAt" }),
      { replace: true }
    );
  };

  return {
    filters,
    searchDraft,
    setSearchDraft,
    rows,
    pagination,
    loading,
    hasActiveFilters: hasActiveAccountFilters(filters),
    updateFilters,
    clearFilters,
    refresh: load
  };
};

export default useAccountList;
